import { PublicKey } from "@solomon-labs/publickey";
import type { SolanaAddress } from "@solomon-labs/types";
import React, { createContext, useCallback, useContext, useMemo, useState } from "react";

import type { SolomonE2EChainOverride, SolomonE2EOverrides, SolomonE2EWalletOverride, WalletState } from "./types";

function readBrowserOverrides(): SolomonE2EOverrides | null {
    if (typeof window === "undefined") {
        return null;
    }

    return window.__SOLOMON_E2E__ ?? null;
}

function normalizeWalletOverride(
    wallet: SolomonE2EWalletOverride | undefined
): SolomonE2EWalletOverride | null {
    if (!wallet) {
        return null;
    }

    return {
        connected: wallet.connected,
        connecting: wallet.connecting ?? false,
        isWalletReady: wallet.isWalletReady ?? wallet.connected,
        address: wallet.address,
        walletName: wallet.walletName ?? null,
        walletIcon: wallet.walletIcon ?? null
    };
}

function parsePublicKey(address: string | null): PublicKey | null {
    if (!address) {
        return null;
    }

    try {
        return new PublicKey(address as SolanaAddress);
    } catch (error) {
        console.error("Failed to parse E2E wallet address:", error);
        return null;
    }
}

const E2EWalletContext = createContext<WalletState | null>(null);

export function readSolomonE2EOverrides(): SolomonE2EOverrides | null {
    return readBrowserOverrides();
}

export function readSolomonE2EChainOverride(): SolomonE2EChainOverride | null {
    return readBrowserOverrides()?.chain ?? null;
}

export function SolomonE2EWalletProvider({
    children
}: {
    children: React.ReactNode;
}): React.ReactElement {
    const [walletOverride, setWalletOverride] = useState<SolomonE2EWalletOverride | null>(() =>
        normalizeWalletOverride(readBrowserOverrides()?.wallet)
    );

    const updateWalletOverride = useCallback(
        (nextWallet: SolomonE2EWalletOverride | null): void => {
            if (typeof window !== "undefined") {
                const current = window.__SOLOMON_E2E__ ?? {};
                if (nextWallet) {
                    window.__SOLOMON_E2E__ = {
                        ...current,
                        wallet: nextWallet
                    };
                } else if (current.chain) {
                    window.__SOLOMON_E2E__ = {
                        chain: current.chain
                    };
                } else {
                    delete window.__SOLOMON_E2E__;
                }
            }

            setWalletOverride(nextWallet);
        },
        []
    );

    const value = useMemo((): WalletState | null => {
        if (!walletOverride) {
            return null;
        }

        return {
            connected: walletOverride.connected,
            connecting: walletOverride.connecting ?? false,
            isWalletReady: walletOverride.isWalletReady ?? walletOverride.connected,
            publicKey: parsePublicKey(walletOverride.address),
            walletName: walletOverride.walletName ?? null,
            walletIcon: walletOverride.walletIcon ?? null,
            disconnect: async (): Promise<void> => {
                updateWalletOverride({
                    ...walletOverride,
                    connected: false,
                    connecting: false,
                    isWalletReady: false,
                    address: null,
                    walletName: null,
                    walletIcon: null
                });
            }
        };
    }, [updateWalletOverride, walletOverride]);

    return (
        <E2EWalletContext.Provider value={value}>
            {children}
        </E2EWalletContext.Provider>
    );
}

export function useSolomonE2EWallet(): WalletState | null {
    return useContext(E2EWalletContext);
}
